// import TableDataNotFound from "component/TableDataNotFound";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import strings from "translate/language";
import {
  ClosingTL,
  CSTL,
  SiteHead,
  isGreRoleId,
} from "utils/constants";
import {
  formatAppointmentCellValue,
  getClosingStatusBadgeClass,
  resolveCheckInDisplay,
  resolveClosingStatusLabel,
} from "utils/appointmentListStatusDisplay";
import { groupAppointmentRows } from "utils/appointmentListGrouping";
import { LATEST_LEAD_STATUS_FILTER_OPTIONS } from "utils/latestLeadStatusFilter";
import StatusBadge, { CheckInStatusBadge } from "component/common/StatusBadge";
import AppointmentGroupHistoryDrawer from "component/common/AppointmentGroupHistoryDrawer";
import { handleListResponse, seo } from "utils/utils";
import { getAppointmentListForAdmin } from "../../reduxStore/action/appointmentActions";
import appointmentService from "../../reduxStore/service/appointmentService";
import Breadcrumb from "component/common/breadcrumb";
import usePermission from "utils/usePermission";
import Pagination from "component/common/Pagination";
import { getReportPropertyList } from "reduxStore/action/reportActions";
import { getAgentProperty } from "reduxStore/action/propertyActions";
import Select from "react-select";
import ExcelJS from "exceljs";
import AllocateSM from "component/common/AllocateCM";
import VisitorCmAllocationCell from "component/common/VisitorCmAllocationCell";
import TableListSkeleton from "component/common/TableListSkeleton";
import {
  getAppointmentCurrentCmId,
  getAppointmentCurrentCmName,
  isAppointmentAllocatedToCm,
} from "utils/visitorCmAllocation";
import "./AppointmentListing.css";

const PAGE_SIZE = 15;

const emptyForm = () => ({
  start_date: "",
  end_date: "",
  limit: "",
  offset: "",
  appointment_with: "",
  customer_name: "",
  status: "",
  property_name: "",
  site: "",
  search_by_mobile_number: "",
  lead_priority: "",
  qualified: "",
  latest_lead_status: "",
});

const renderAppointmentCell = (value, toneClass = "") => {
  const text = formatAppointmentCellValue(value);
  const tone = text === "-" ? "appointment-listing__empty" : toneClass;
  return (
    <span className={`appointment-listing__cell ${tone}`.trim()}>{text}</span>
  );
};

const renderDateTimeCell = (dateValue, timeValue = "") => {
  if (!dateValue && !timeValue) {
    return <span className="appointment-listing__empty">-</span>;
  }
  return (
    <span className="appointment-listing__date-stack">
      {dateValue ? (
        <span className="appointment-listing__date-day">{dateValue}</span>
      ) : null}
      {timeValue ? (
        <span className="appointment-listing__date-time">{timeValue}</span>
      ) : null}
    </span>
  );
};

const Appointment = () => {
  const navigate = useNavigate();
  const navigatpage = async (navname) => {
    navigate(navname);
  };
  const dispatch = useDispatch();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const appointmentListSelector = useSelector((state) => state.appointment);
  const ReportListSelector = useSelector((state) => state.report);
  const propertySelector = useSelector((state) => state.propertyData) || {};
  // Super-admin list uses response1. Non-admin Appointment Management loads into
  // local state (see fetchAppointmentManagementList) so Dashboard's shared
  // GET_APPOINTMENT_LIST (status:3 / type:2) cannot wipe View History rows.
  const { response1 } = appointmentListSelector;

  // Local calendar YYYY-MM-DD (toISOString() uses UTC and shifts the day in IST and similar zones).
  const formatDate = (date) => moment(date).format("YYYY-MM-DD");
  const getDefaultDateRange = () => {
    const todayStr = formatDate(new Date());
    return { start_date: todayStr, end_date: todayStr };
  };

  const { view, create, edit, isAdmin } = usePermission({
    view: "view_appointment",
    create: "add_appointment",
    edit: "edit_appointment",
  });

  const [form, setForm] = useState(emptyForm);
  const appliedFiltersRef = useRef(null);

  const [formVaild, setFormVaild] = useState({});
  const [endDateDisable, setEndDateDisable] = useState(true);
  const [appointmentData, setAppointmentData] = useState([]);
  const [propertyList, setPropertyList] = useState([]);
  const [res, setRes] = useState(true);
  const [listReady, setListReady] = useState(false);
  const [revistCheck, setRevistCheck] = useState(false);
  const [page, setPage] = useState(0);
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [historyDrawer, setHistoryDrawer] = useState(null);
  const { response: loginResponse } = useSelector((state) => state.login);
  const roleId = loginResponse?.data?.role_id;
  const currentRoleId = String(roleId || "").trim();
  const closingTlRoleId = String(ClosingTL || "").trim();
  const cstlRoleId = String(CSTL || "").trim();
  const siteHeadRoleId = String(SiteHead || "").trim();
  const canAllocateToCM =
    isGreRoleId(roleId) ||
    currentRoleId === closingTlRoleId ||
    currentRoleId === cstlRoleId ||
    currentRoleId === siteHeadRoleId;
  const applyAppointmentListPayload = (payload) => {
    if (payload?.status === 200) {
      const data = payload?.data || [];
      setRes(false);
      setListReady(true);
      // Appointment Management: keep list stable & intuitive by sorting by Created Date (newest first).
      // Backend sorts by appointment_date/time which can place newly-created revisits in the middle.
      const safe = Array.isArray(data) ? data.slice() : [];
      safe.sort((a, b) => {
        const ta = a?.createdDate ? new Date(a.createdDate).getTime() : 0;
        const tb = b?.createdDate ? new Date(b.createdDate).getTime() : 0;
        if (tb !== ta) return tb - ta;
        const da = a?.appointment_date
          ? new Date(a.appointment_date).getTime()
          : 0;
        const db = b?.appointment_date
          ? new Date(b.appointment_date).getTime()
          : 0;
        if (db !== da) return db - da;
        return String(b?._id || "").localeCompare(String(a?._id || ""));
      });
      const cleaned = handleListResponse(safe);
      setAppointmentData(Array.isArray(cleaned) ? cleaned : []);
    } else {
      setRes(false);
      setListReady(true);
      setAppointmentData([]);
    }
  };

  // Appointment Management must return all matching rows so lead+property
  // grouping can populate View History for every role with this page permission
  // (not only super admin / getAppointmentListForAdmin). Never send
  // appointment_type: 2 — that backend path keeps only the latest row per lead.
  // Pagination is client-side (PAGE_SIZE) after grouping so history stays intact.
  const getAppointmentListParams = (source = form, overrides = {}) => ({
    appointment_type: 1,
    limit: "",
    offset: 0,
    start_date: source.start_date || "",
    end_date: source.end_date || "",
    appointment_with: source.appointment_with || "",
    customer_name: (source.customer_name || "").trim(),
    status: source.status || "",
    property_name: source?.site?.property_title || "",
    noData: true,
    search_by_mobile_number: (source.search_by_mobile_number || "").trim(),
    qualified: source.qualified ?? "",
    lead_priority: source.lead_priority || "",
    latest_lead_status: source.latest_lead_status || "",
    ...overrides,
  });

  const fetchAppointmentManagementList = async (params) => {
    setRes(true);
    appliedFiltersRef.current = params;
    if (isAdmin) {
      dispatch(getAppointmentListForAdmin(params));
      return;
    }
    try {
      const listRes = await appointmentService.getAppointmentList(params);
      applyAppointmentListPayload(listRes?.data);
    } catch (e) {
      setRes(false);
      setListReady(true);
      setAppointmentData([]);
    }
  };

  // Initial Fetch on Mount
  useEffect(() => {
    const queryStart = query.get("start_date");
    const queryEnd = query.get("end_date");
    const queryStatus = query.get("status") || "";
    const queryLatestLeadStatus = query.get("latest_lead_status") || "";
    const hiddenStatus = location.state?.hiddenStatus || queryStatus || "";
    const defaults = getDefaultDateRange();

    const defaultStart = queryStart || defaults.start_date;
    const defaultEnd = queryEnd || defaults.end_date;

    const initialForm = {
      ...emptyForm(),
      start_date: defaultStart,
      end_date: defaultEnd,
      status: hiddenStatus,
      latest_lead_status: queryLatestLeadStatus,
    };

    setForm(initialForm);
    setPage(0);
    setRes(true);
    setListReady(false);

    const params = getAppointmentListParams(initialForm, {
      status: hiddenStatus,
      latest_lead_status: queryLatestLeadStatus,
    });
    fetchAppointmentManagementList(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount / role / dashboard deep-link
  }, [dispatch, isAdmin, location.search]);

  // Super-admin Response Handler (response1)
  useEffect(() => {
    if (!isAdmin) return;
    if (!response1) return;
    applyAppointmentListPayload(response1);
  }, [isAdmin, response1]);

  const appointmentGroups = useMemo(
    () => groupAppointmentRows(appointmentData),
    [appointmentData],
  );

  const pagedGroups = useMemo(() => {
    const start = page * PAGE_SIZE;
    return appointmentGroups.slice(start, start + PAGE_SIZE);
  }, [appointmentGroups, page]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(appointmentGroups.length / PAGE_SIZE) - 1,
    );
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [appointmentGroups.length, page]);

  // Admin Property List
  useEffect(() => {
    if (isAdmin) {
      const { reportPropertyList } = ReportListSelector;
      if (reportPropertyList?.status === 200) {
        setPropertyList(handleListResponse(reportPropertyList.data));
      } else {
        setPropertyList([]);
      }
    }
  }, [ReportListSelector, isAdmin]);

  // Non-Admin Property List Fix
  useEffect(() => {
    const agentResp = propertySelector.agentPropertyResponse;
    const adminResp = propertySelector.response;
    const currentResp = isAdmin ? adminResp : agentResp || adminResp;

    if (currentResp?.status === 200) {
      const filteredData = currentResp.data.filter(
        (item) => item.status === true,
      );
      setPropertyList(handleListResponse(filteredData || []));
    } else {
      setPropertyList([]);
    }
  }, [propertySelector, isAdmin]);

  // Dispatch property list based on role
  useEffect(() => {
    if (isAdmin) {
      dispatch(getReportPropertyList({ limit: "", offset: 0 }));
    } else {
      dispatch(getAgentProperty({ offset: 0, limit: "" }));
    }
  }, [dispatch, isAdmin]);

  const handleValid = () => {
    let err = { ...formVaild };
    let formError = false;

    if (form.start_date && !form.end_date) {
      formError = true;
      err["end_date"] = strings.endDateErr || "End date is required";
    }

    setFormVaild(err);
    return formError;
  };

  const refreshAppointmentList = () => {
    const params =
      appliedFiltersRef.current || getAppointmentListParams(form);
    fetchAppointmentManagementList(params);
  };

  const filterDetail = () => {
    if (handleValid()) return;
    if (isAdmin) {
      setRevistCheck(form.status === "12");
    }
    setPage(0);
    fetchAppointmentManagementList(getAppointmentListParams(form));
  };

  const filterReset = () => {
    const defaults = getDefaultDateRange();

    const resetForm = {
      ...emptyForm(),
      start_date: defaults.start_date,
      end_date: defaults.end_date,
    };

    setForm(resetForm);
    setFormVaild({ end_date: "" });
    setPage(0);
    setRes(true);
    setListReady(false);
    setRevistCheck(false);

    fetchAppointmentManagementList(getAppointmentListParams(resetForm));
  };

  const handleInputField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "end_date") {
      setFormVaild((prev) => ({
        ...prev,
        end_date: "",
      }));
    }
  };

  const handlePageClick = (newPage) => {
    setPage(newPage);
  };

  useEffect(() => {
    setEndDateDisable(form.start_date === "");
  }, [form.start_date]);

  useEffect(() => {
    seo({
      title: strings.appointmentManagement,
      metaDescription: strings.appointmentList,
      canonicalLink: window.location.href,
    });
  }, []);

  // const handlePageClick = (newOffset) => {
  //   setPage(newOffset);
  //   dispatch(
  //     getAppointmentList({
  //       appointment_type: 2,
  //       limit: "",
  //       offset: newOffset,
  //       start_date: form.start_date,
  //       end_date: form.end_date,
  //       appointment_with: form.appointment_with,
  //       customer_name: form.customer_name,
  //       status: form.status,
  //       property_name: form?.site?.property_title,
  //       search_by_mobile_number: for,.search_by_mobile_number,
  //     })
  //   );
  // };

  const exportToExcel = async () => {
    // Create a new workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Appointments");

    // Define columns for Excel
    worksheet.columns = [
      { header: "Sr.No", key: "srno", width: 10 },
      { header: "Created Date & Time", key: "createdDate", width: 25 },
      { header: "Site Visit Time & Date", key: "siteVisit", width: 25 },
      { header: "Property Name", key: "property", width: 25 },
      { header: "Customer Name", key: "customer", width: 20 },
      { header: "Source", key: "source", width: 20 },
      { header: "Closing Status", key: "closingStatus", width: 20 },
      { header: "Check-In Status", key: "checkInStatus", width: 20 },
      { header: "Visiting Score", key: "visitingScore", width: 15 },
    ];

    // Map appointment data to rows
    const data = appointmentData.map((item, index) => {
      const checkInDisplay = resolveCheckInDisplay(item);

      // Convert the appointment date to a readable format
      const siteVisitTime = item.appointment_date
        ? moment(item.appointment_date).utc().format("DD-MM-yyyy") +
          " & " +
          (item.appointment_time || "")
        : "";
      const createdDate = item.createdDate
        ? moment(item.createdDate).format("DD-MM-yyyy hh:mm A")
        : "";
      const closingRaw = resolveClosingStatusLabel(item);
      const closingStatus = formatAppointmentCellValue(closingRaw);

      // Return row data
      return {
        srno: index + 1,
        createdDate: formatAppointmentCellValue(createdDate),
        siteVisit: formatAppointmentCellValue(siteVisitTime),
        property: formatAppointmentCellValue(item?.property_title),
        customer: formatAppointmentCellValue(
          `${item?.customer_first_name || ""} ${item?.customer_last_name || ""}`.trim(),
        ),
        source: formatAppointmentCellValue(item?.create_by),
        closingStatus: closingStatus,
        checkInStatus: formatAppointmentCellValue(checkInDisplay.label),
        visitingScore: formatAppointmentCellValue(item?.lead_score),
      };
    });

    // Add rows to the worksheet
    worksheet.addRows(data);

    worksheet.getRow(1).font = { bold: true };
    // Generate the Excel file
    const buffer = await workbook.xlsx.writeBuffer();

    // Create a Blob object with the Excel file content
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    // Create a temporary download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "appointments_report.xlsx"; // The filename of the Excel file
    link.click();
  };
  return (
    <div className="page-body justo-appointment-page">
      <Breadcrumb
        title={strings.appointmentManagement}
        parentListing={null}
        titleactive={strings.appointmentList}
      />
      <div className="container-fluid justo-appointment">
        <div className="justo-appointment__card">
          <div className="justo-appointment__top">
            <div className="justo-appointment__title-block">
              <h1 className="justo-appointment__title">
                {strings.appointmentList}
              </h1>
              <p className="justo-appointment__subtitle">
                {strings.appointmentListSubtitle}
              </p>
            </div>
            <div className="justo-appointment__top-actions">
              {listReady && appointmentGroups.length > 0 ? (
                <span className="justo-appointment__count-chip">
                  <strong>{appointmentGroups.length}</strong> appointments
                </span>
              ) : null}
              {!isAdmin && create ? (
                <>
                  <button
                    type="button"
                    className="justo-appointment__btn justo-appointment__btn--solid"
                    onClick={() => navigatpage("/add_appointment")}
                  >
                    {strings.addAppointment}
                  </button>
                  <button
                    type="button"
                    className="justo-appointment__btn justo-appointment__btn--outline"
                    onClick={() => navigatpage("/today_appointments")}
                  >
                    {strings.todayAppointments}
                  </button>
                </>
              ) : null}
              {appointmentData.length > 0 ? (
                <button
                  type="button"
                  className="justo-appointment__btn justo-appointment__btn--outline"
                  onClick={exportToExcel}
                >
                  Export to Excel
                </button>
              ) : null}
            </div>
          </div>

          <form
            className="justo-appointment__filters"
            onSubmit={(e) => {
              e.preventDefault();
              filterDetail();
            }}
          >
            <div className="justo-appointment__filters-grid">
              <label className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.startDate}
                </span>
                <input
                  className="form-control justo-appointment__input"
                  type="date"
                  name="start_date"
                  value={form.start_date}
                  onChange={handleInputField}
                />
              </label>
              <label className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.endDate}
                </span>
                <input
                  className="form-control justo-appointment__input"
                  type="date"
                  name="end_date"
                  value={form.end_date}
                  onChange={handleInputField}
                  min={form.start_date}
                  disabled={endDateDisable}
                />
                {formVaild.end_date ? (
                  <span className="justo-appointment__error">
                    {formVaild.end_date}
                  </span>
                ) : null}
              </label>
              <label className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.searchByCustomerName}
                </span>
                <input
                  className="form-control justo-appointment__input"
                  type="text"
                  autoComplete="off"
                  placeholder={strings.searchCustomerName}
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleInputField}
                />
              </label>
              <label className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.searchByStatus}
                </span>
                <select
                  className="form-control justo-appointment__input"
                  name="status"
                  value={form.status}
                  onChange={handleInputField}
                >
                  <option value="">{strings.selectstatus}</option>
                  <option value={1}>{strings.panding}</option>
                  <option value={2}>{strings.revisit}</option>
                  <option value={3}>{strings.actualvisit}</option>
                  <option value={4}>{strings.visitcancelled}</option>
                  <option value={5}>{strings.rescheduled}</option>
                  <option value={6}>{strings.notfitforseal}</option>
                  {isAdmin ? (
                    <option value={12}>Revisit Completed</option>
                  ) : null}
                </select>
              </label>
              <div className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.searchByPropertyName}
                </span>
                <Select
                  classNamePrefix="justo-appointment-select"
                  isSearchable
                  isClearable
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                  onInputChange={(e) => {
                    if (isAdmin) {
                      if (e.length > 1) {
                        dispatch(
                          getReportPropertyList({
                            search_site: e,
                            isLoadMore: true,
                          }),
                        );
                      } else {
                        dispatch(
                          getReportPropertyList({
                            limit: "",
                            offset: 0,
                            isLoadMore: true,
                          }),
                        );
                      }
                    }
                  }}
                  placeholder={strings.searchByPropertyName}
                  options={propertyList}
                  getOptionLabel={(option) => `${option.property_title}`}
                  getOptionValue={(option) => option._id}
                  name="site"
                  value={form.site || null}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, site: e || "" }));
                  }}
                />
              </div>
              <label className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.searchByMobileNumber}
                </span>
                <input
                  className="form-control justo-appointment__input"
                  type="text"
                  autoComplete="off"
                  placeholder={strings.searchMobileNumber}
                  name="search_by_mobile_number"
                  value={form.search_by_mobile_number}
                  onChange={handleInputField}
                  maxLength="10"
                />
              </label>
              <label className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.latestLeadStatus}
                </span>
                <select
                  className="form-control justo-appointment__input"
                  name="latest_lead_status"
                  onChange={handleInputField}
                  value={form.latest_lead_status}
                >
                  <option value="">{strings.selectLatestLeadStatus}</option>
                  {LATEST_LEAD_STATUS_FILTER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.visitorStatusFilter}
                </span>
                <select
                  className="form-control justo-appointment__input"
                  name="qualified"
                  onChange={handleInputField}
                  value={form.qualified}
                >
                  <option value="">{strings.selectVisitorStatus}</option>
                  <option value="true">{strings.qualified}</option>
                  <option value="false">{strings.unQualified}</option>
                </select>
              </label>
              <label className="justo-appointment__field">
                <span className="justo-appointment__label">
                  {strings.leadPriority}
                </span>
                <select
                  className="form-control justo-appointment__input"
                  name="lead_priority"
                  onChange={handleInputField}
                  value={form.lead_priority}
                >
                  <option value="">{strings.selectLeadPriority}</option>
                  <option value="hot">{strings.hot}</option>
                  <option value="warm">{strings.warm}</option>
                  <option value="cold">{strings.cold}</option>
                </select>
              </label>
            </div>
            {(form.customer_name?.trim() ||
              form.search_by_mobile_number?.trim()) && (
              <p
                className="justo-appointment__visitor-search-hint"
                style={{
                  margin: "0 0 12px",
                  fontSize: "0.8rem",
                  fontWeight: 400,
                  color: "#64748b",
                }}
              >
                {strings.depositionVisitorSearchHint}
              </p>
            )}
            <div className="justo-appointment__filter-actions">
              <button
                className="justo-appointment__btn justo-appointment__btn--solid"
                type="button"
                onClick={filterDetail}
              >
                <i className="fa fa-search" aria-hidden="true" />
                {strings.search}
              </button>
              <button
                className="justo-appointment__btn justo-appointment__btn--ghost"
                type="button"
                onClick={filterReset}
              >
                {strings.reset}
              </button>
            </div>
          </form>

          <div className="justo-appointment__table-wrap">
            <div className="table-responsive appointment-listing--hybrid__scroll">
              {res && !listReady ? (
                <TableListSkeleton
                  rows={PAGE_SIZE}
                  columns={canAllocateToCM ? 11 : 10}
                />
              ) : listReady && appointmentGroups.length === 0 ? (
                <div className="justo-appointment__empty-state">
                  No data available
                </div>
              ) : (
                <table className="table appointment-listing--hybrid__table">
                        <thead>
                          <tr>
                            <th
                              scope="col"
                              className="appointment-listing__text-center appointment-listing__date-col"
                            >
                              Created
                            </th>
                            <th
                              scope="col"
                              className="appointment-listing__text-center appointment-listing__date-col"
                            >
                              {revistCheck ? "Revisit" : "Site visit"}
                            </th>
                            <th
                              scope="col"
                              className="appointment-listing__text-left appointment-listing__property-col"
                            >
                              {strings.property_name}
                            </th>
                            <th
                              scope="col"
                              className="appointment-listing__text-left appointment-listing__visitor-col"
                            >
                              Visitor
                            </th>
                            <th
                              scope="col"
                              className="appointment-listing__text-left appointment-listing__source-col"
                            >
                              {strings.source}
                            </th>
                            <th
                              scope="col"
                              className="appointment-listing__text-center appointment-listing__status-col"
                            >
                              Closing
                            </th>
                            <th
                              scope="col"
                              className="appointment-listing__text-center appointment-listing__status-col"
                            >
                              Check-in
                            </th>
                            <th
                              scope="col"
                              className="appointment-listing__text-center appointment-listing__score-col"
                            >
                              Score
                            </th>
                            {canAllocateToCM ? (
                              <th
                                scope="col"
                                className="appointment-listing__text-center"
                              >
                                CM
                              </th>
                            ) : null}
                            <th
                              scope="col"
                              className="appointment-listing__text-center appointment-listing__history-col"
                            >
                              History
                            </th>
                            <th
                              scope="col"
                              className={`appointment-listing__text-center appointment-listing__actions-col${
                                edit && !isAdmin && view
                                  ? " appointment-listing__actions-col--wide"
                                  : ""
                              }`}
                            >
                              {strings.action}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedGroups.map(({ key, primary, others }) => {
                            const item = primary;
                            const statusHistoryCount = Number(
                              item?.status_history_count || 0,
                            );
                            const hasHistory = statusHistoryCount > 0;
                            const checkInDisplay = resolveCheckInDisplay(item);
                            const showAsVisited = checkInDisplay.showAsVisited;
                            const showAllocateAction =
                              canAllocateToCM &&
                              item?.property_id &&
                              item?.lead_id &&
                              Number(item?.lead_status) <= 3;
                            const appointmentAllocatedToCm =
                              isAppointmentAllocatedToCm(item);
                            const showReallocateAction =
                              showAllocateAction && appointmentAllocatedToCm;
                            const showFirstAllocateAction =
                              showAllocateAction && !appointmentAllocatedToCm;
                            const currentCmId =
                              getAppointmentCurrentCmId(item);
                            const currentCmName =
                              getAppointmentCurrentCmName(item);
                            const visitorName =
                              `${item?.customer_first_name || ""} ${item?.customer_last_name || ""}`.trim();
                            const closingLabel =
                              resolveClosingStatusLabel(item);
                            const showEditAction =
                              item.status === 1 && !isAdmin && edit;
                            const actionsColClassName =
                              showEditAction && view
                                ? "appointment-listing__text-center appointment-listing__actions-col appointment-listing__actions-col--wide"
                                : "appointment-listing__text-center appointment-listing__actions-col";

                            return (
                              <tr key={key}>
                                <td className="appointment-listing__text-center appointment-listing__date-col">
                                  {renderDateTimeCell(
                                    item.createdDate
                                      ? moment(item.createdDate).format(
                                          "DD-MM-YYYY",
                                        )
                                      : "",
                                    item.createdDate
                                      ? moment(item.createdDate).format(
                                          "hh:mm A",
                                        )
                                      : "",
                                  )}
                                </td>
                                <td className="appointment-listing__text-center appointment-listing__date-col">
                                  {renderDateTimeCell(
                                    item.appointment_date
                                      ? moment(item.appointment_date)
                                          .utc()
                                          .format("DD-MM-YYYY")
                                      : "",
                                    item.appointment_time || "",
                                  )}
                                </td>
                                <td className="appointment-listing__text-left appointment-listing__property-col">
                                  {renderAppointmentCell(
                                    item?.property_title,
                                    "appointment-listing__primary",
                                  )}
                                </td>
                                <td className="appointment-listing__text-left appointment-listing__visitor-col">
                                  {renderAppointmentCell(
                                    visitorName,
                                    "appointment-listing__primary",
                                  )}
                                </td>
                                <td className="appointment-listing__text-left appointment-listing__source-col">
                                  {renderAppointmentCell(
                                    item?.create_by,
                                    "appointment-listing__muted",
                                  )}
                                </td>
                                <td className="appointment-listing__text-center appointment-listing__status-col">
                                  <StatusBadge
                                    label={closingLabel}
                                    badgeClass={getClosingStatusBadgeClass(
                                      closingLabel,
                                    )}
                                    size="sm"
                                  />
                                </td>
                                <td className="appointment-listing__text-center appointment-listing__status-col">
                                  <CheckInStatusBadge
                                    showAsVisited={showAsVisited}
                                    label={checkInDisplay.label}
                                  />
                                </td>
                                <td className="appointment-listing__text-center">
                                  {renderAppointmentCell(
                                    item?.lead_score,
                                    "appointment-listing__metric",
                                  )}
                                </td>
                                {canAllocateToCM ? (
                                  <td className="appointment-listing__text-center appointment-listing__allocate-col">
                                    <VisitorCmAllocationCell
                                      strings={strings}
                                      disabled={showAllocateModal}
                                      cmName={currentCmName}
                                      state={
                                        showReallocateAction
                                          ? "allocated"
                                          : showFirstAllocateAction
                                            ? "pending"
                                            : "inactive"
                                      }
                                      onAllocate={() => {
                                        setSelectedVisitor({
                                          leadId: item.lead_id,
                                          propertyId: item.property_id,
                                          visitorName,
                                          isReallocate: false,
                                        });
                                        setShowAllocateModal(true);
                                      }}
                                      onReallocate={() => {
                                        setSelectedVisitor({
                                          leadId: item.lead_id,
                                          propertyId: item.property_id,
                                          visitorName,
                                          currentCmId,
                                          currentCmName,
                                          isReallocate: appointmentAllocatedToCm,
                                        });
                                        setShowAllocateModal(true);
                                      }}
                                    />
                                  </td>
                                ) : null}
                                <td className="appointment-listing__text-center appointment-listing__history-col">
                                  <div className="appointment-listing__icon-col-inner">
                                    <span className="appointment-listing__history-slot">
                                      <button
                                        type="button"
                                        className="appointment-listing__action-btn appointment-listing__history-icon-btn"
                                        title={
                                          hasHistory
                                            ? `${strings.viewHistory} (${statusHistoryCount})`
                                            : strings.viewHistory
                                        }
                                        aria-label={
                                          hasHistory
                                            ? `${strings.viewHistory} (${statusHistoryCount})`
                                            : strings.viewHistory
                                        }
                                        onClick={() =>
                                          setHistoryDrawer({
                                            key,
                                            primary,
                                            others,
                                          })
                                        }
                                      >
                                        <i
                                          className="fa fa-history"
                                          aria-hidden="true"
                                        />
                                        {hasHistory ? (
                                          <span
                                            className="appointment-listing__history-badge"
                                            aria-hidden="true"
                                          >
                                            {statusHistoryCount}
                                          </span>
                                        ) : null}
                                      </button>
                                    </span>
                                  </div>
                                </td>
                                <td className={actionsColClassName}>
                                  <div className="appointment-listing__icon-col-inner">
                                    <div className="appointment-listing__action-group">
                                      {view ? (
                                        <button
                                          type="button"
                                          className="appointment-listing__action-btn"
                                          title="View Appointment"
                                          onClick={() =>
                                            navigatpage(
                                              `/viewappointment/${item._id}`,
                                            )
                                          }
                                        >
                                          <i
                                            className="fa fa-eye"
                                            aria-hidden="true"
                                          />
                                        </button>
                                      ) : null}
                                      {showEditAction ? (
                                        <button
                                          type="button"
                                          className="appointment-listing__action-btn"
                                          title="Edit Appointment"
                                          onClick={() =>
                                            navigatpage(
                                              `/editappointment/${item._id}`,
                                            )
                                          }
                                        >
                                          <i
                                            className="fa fa-edit"
                                            aria-hidden="true"
                                          />
                                        </button>
                                      ) : null}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
            </div>
            {listReady && appointmentGroups.length > 0 ? (
              <Pagination
                itemsPerPage={PAGE_SIZE}
                totalCount={appointmentGroups.length}
                itemOffset={page}
                listData={pagedGroups}
                onPageChange={handlePageClick}
                setItemOffset={setPage}
              />
            ) : null}
          </div>
        </div>
      </div>

      <AppointmentGroupHistoryDrawer
        show={Boolean(historyDrawer)}
        primary={historyDrawer?.primary}
        onClose={() => setHistoryDrawer(null)}
        onView={
          view ? (id) => navigatpage(`/viewappointment/${id}`) : null
        }
      />

      {canAllocateToCM && selectedVisitor && (
        <AllocateSM
          show={showAllocateModal}
          propertyId={selectedVisitor.propertyId}
          leadId={selectedVisitor.leadId}
          visitorName={selectedVisitor.visitorName}
          isReallocate={selectedVisitor.isReallocate}
          currentCmId={selectedVisitor.currentCmId}
          currentCmName={selectedVisitor.currentCmName}
          handleClose={() => {
            setShowAllocateModal(false);
            setSelectedVisitor(null);
          }}
          onAllocateSuccess={() => {
            setShowAllocateModal(false);
            setSelectedVisitor(null);
            refreshAppointmentList();
          }}
        />
      )}
    </div>
  );
};
export default Appointment;
